import { Fragment, useMemo, useRef, useState } from 'react';
import { Box, RadioGroup, Typography } from '@mui/material';
import { SwipeableCustomerDrawer, CustomerContainer, Button, Divider } from '@qlub-dev/qlub-kit';
import { setUserLanguage } from '../../../utility/k_lang';
import { useLanguage } from '../../../const';
import { useConfigContext } from '../../../context/config';
import { EnableEnum } from '../../../repository/vendor';
import { onPushEvent } from '../../../lib/gtm';
import { useQlubRouter } from '../../../hook/router';
import LanguageSwitcherLine from '../LanguageSwitcherLine';
import getLanguageData from '../language-data';
import { useLanguageSwitcherEvents } from '../hooks/useLanguageSwitcherEvents';

import styles from './index.module.scss';

interface IProps {
    drawerTitle?: string;
    variant?: 'default' | 'no-flag';
}

function Drawer({ drawerTitle, variant }: IProps) {
    const radioGroupRef = useRef();
    const { url, pathname, source, routerPush, query } = useQlubRouter();
    const [selected, setSelected] = useState<string>(query.lang || 'en');
    const { drawerOpen, dispatchDrawerState } = useLanguageSwitcherEvents();
    const { languages } = useLanguage();
    const { config: countryConfig } = useConfigContext();

    const countryListType =
        variant !== undefined
            ? variant
            : countryConfig?.hideCountryFlagsFromLanguageSelector === EnableEnum.Enable
              ? 'no-flag'
              : 'default';

    const countryCode = url.cc;

    const langData = useMemo(() => {
        const languageData = getLanguageData(countryCode);
        if (!selected) {
            return languageData[0];
        }
        return languageData.find((i) => i.key === selected);
    }, [selected, countryCode]);

    const langChangeHandler = (e: string) => {
        if (selected === e) return selected;
        setSelected(e);
    };

    const cancelLanguageSwitch = () => {
        dispatchDrawerState(false);
        setSelected(query.lang || 'en');
    };
    const onLanguageSwitcherClicked = () => {
        onPushEvent('intent_to_change_language', {
            restaurant_name: url.slug,
            source,
        });
    };
    const onChange = (language: string) => {
        if (language !== url.cc) {
            onPushEvent('confirm_change_language', {
                restaurant_name: url.slug,
                source,
            });
        }

        setUserLanguage(language);

        routerPush(pathname, { ...query, lang: language }, { shallow: true });
    };

    const onOpen = () => {
        dispatchDrawerState(true);
        if (onLanguageSwitcherClicked) {
            onLanguageSwitcherClicked();
        }
    };
    const onClose = () => {
        dispatchDrawerState(false);
        setSelected(query.lang || 'en');
    };

    const confirmLanguageSwitch = () => {
        dispatchDrawerState(false);
        onChange?.(selected);
    };

    return (
        <SwipeableCustomerDrawer
            headerTitle={langData?.text?.changeLanguage || drawerTitle}
            open={drawerOpen}
            hasOverflow
            hasRadius={false}
            className={styles.SwipeableCustomerDrawer}
            onOpen={onOpen}
            onClose={onClose}
            contentStyle={{
                padding: 0,
            }}
            qaIds={{
                closeBtnId: 'close-language',
            }}
        >
            <CustomerContainer className={styles.container}>
                <Box>
                    <RadioGroup
                        onChange={(_, value) => langChangeHandler(value)}
                        ref={radioGroupRef}
                        name="use-radio-group"
                    >
                        {languages.map((item, index) => {
                            return (
                                <Fragment key={`${item.title}-${index}`}>
                                    <Divider />
                                    <LanguageSwitcherLine
                                        language={item}
                                        variant={countryListType}
                                        onChange={() => langChangeHandler(item.value)}
                                        selected={selected === item.value}
                                        countryCode={countryCode}
                                    />
                                </Fragment>
                            );
                        })}
                    </RadioGroup>
                </Box>
                <Divider />
                <Box className={styles.languageSwitcherButtonContainer}>
                    <Button
                        data-qa-id="cancel-language"
                        data-testid="button"
                        variant="containedLight"
                        className={styles.languageChangeButtons}
                        onClick={() => cancelLanguageSwitch()}
                        fullWidth
                    >
                        <Typography>{langData?.text?.cancel}</Typography>
                    </Button>
                    <Button
                        data-qa-id="confirm-language"
                        data-testid="button"
                        variant="contained"
                        className={styles.languageChangeButtons}
                        onClick={() => confirmLanguageSwitch()}
                        fullWidth
                    >
                        <Typography>{langData?.text?.confirm}</Typography>
                    </Button>
                </Box>
            </CustomerContainer>
        </SwipeableCustomerDrawer>
    );
}

export default Drawer;
